// Celebración al registrar un pedido: qué tan grande es y qué mensaje lleva. Funciones puras, con pruebas.
// El dibujo (confeti y pirotecnia) está en componentes/celebracion.ts.
import { money } from './dinero';

export type NivelCelebracion = 'pedido' | 'meta5' | 'meta10';

// Ventas netas del mes, en pesos, que activan la celebración grande (las pidió Felipe el 6 oct).
export const HITO_5M = 5_000_000;
export const HITO_10M = 10_000_000;

export interface PlanCelebracion {
  duracion: number;   // milisegundos de confeti y pirotecnia
  aviso: number;      // milisegundos que el mensaje queda a la vista (más que el dibujo, para alcanzar a leerlo)
  confeti: number;    // piezas de confeti
  estallidos: number; // explosiones de pirotecnia
  chispas: number;    // chispas por explosión
}
export const PLANES: Record<NivelCelebracion, PlanCelebracion> = {
  pedido: { duracion: 2400, aviso: 3200, confeti: 160, estallidos: 5, chispas: 56 },
  meta5: { duracion: 3600, aviso: 5200, confeti: 260, estallidos: 9, chispas: 70 },
  meta10: { duracion: 4800, aviso: 6500, confeti: 380, estallidos: 14, chispas: 84 },
};

// Compara las ventas netas del mes antes y después de guardar. Solo cuenta cruzar un hito (de "no más de" a "más de");
// si ya estaba por encima, el pedido se celebra como uno normal. Si no se pudo calcular alguna de las dos, también.
// Si un solo pedido cruza los dos hitos, gana el de 10 millones.
export function nivelCelebracion(antes: number | null, despues: number | null): NivelCelebracion {
  if (antes == null || despues == null) return 'pedido';
  if (antes <= HITO_10M && despues > HITO_10M) return 'meta10';
  if (antes <= HITO_5M && despues > HITO_5M) return 'meta5';
  return 'pedido';
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export const nombreMes = (mes: string) => MESES[+mes.slice(5, 7) - 1] || '';

const FRASES_PEDIDO = [
  'Otro dibujo que se vuelve prenda.',
  'Otra familia que va a sonreír.',
  'Así se hace: un pedido más en camino.',
  'Cada pedido cuenta, y este ya está en la lista.',
  'Un paso más hacia un mes redondo.',
];

// variante: número para escoger la frase (en el uso real, al azar; en las pruebas, fijo).
export function mensajeCelebracion(nivel: NivelCelebracion, o: { id?: string; mes?: string; variante?: number } = {}): string {
  const mes = o.mes ? nombreMes(o.mes) : '';
  const de = mes ? ` de ${mes}` : ' del mes';
  if (nivel === 'meta10') return `🏆 ¡Superaste los ${money(HITO_10M)} en ventas${de}! Lo que empezó con un dibujo hoy llena de color todo el mes. Qué orgullo: sigue así.`;
  if (nivel === 'meta5') return `🎉 ¡Superaste los ${money(HITO_5M)} en ventas${de}! Cada pedido es un dibujo que se vuelve realidad. Vas por muy buen camino.`;
  const v = Math.abs(Math.floor(o.variante ?? 0)) % FRASES_PEDIDO.length;
  return `🎉 ¡${o.id ? `${o.id} guardado` : 'Pedido guardado'}! ${FRASES_PEDIDO[v]}`;
}

// Mes ('AAAA-MM') de una fecha ISO, y el siguiente (para pedir solo los pedidos de un mes).
export const mesDe = (fecha: string) => fecha.slice(0, 7);
export const mesSiguiente = (mes: string) => { const y = +mes.slice(0, 4), m = +mes.slice(5, 7); return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`; };
